import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ClientLayout from "../components/ClientLayout";
import OperationProcessStepper from "../components/OperationProcessStepper";
import { getPurchaseOrder, getPurchaseOrderOperationStatus } from "../api/clientPortal";
import { useSnack } from "../services/SnakbarProvider";
import { handleError } from "../services/errorHandler";

const Field = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value ?? "-"}</Typography>
  </Grid>
);

const formatFinishedGood = (shipment) => {
  const code = shipment.finishedGoodCode;
  const description =
    shipment.finishedGoodDescription || shipment.finishedGoodName;
  if (code && description) return `${code} — ${description}`;
  return code || description || "-";
};

export default function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openSnack } = useSnack();
  const [cpo, setCpo] = useState(null);
  const [operationStatus, setOperationStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cpoRes = await getPurchaseOrder(id);
        setCpo(cpoRes.data);

        try {
          const statusRes = await getPurchaseOrderOperationStatus(id);
          setOperationStatus(statusRes.data);
        } catch (e) {
          if (e?.response?.status !== 404) throw e;
        }
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const shipments = useMemo(() => {
    if (!cpo) return [];
    if (cpo.cpoShipments?.length) return cpo.cpoShipments;

    return (cpo.details || []).flatMap((detail) =>
      (detail.shipments || []).map((shipment) => ({
        ...shipment,
        finishedGoodName:
          shipment.finishedGoodName ?? detail.finishedGood?.name,
        finishedGoodDescription:
          shipment.finishedGoodDescription ??
          detail.finishedGood?.description,
        finishedGoodCode:
          shipment.finishedGoodCode ?? detail.finishedGood?.code,
      }))
    );
  }, [cpo]);

  if (loading) {
    return (
      <ClientLayout>
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      </ClientLayout>
    );
  }

  if (!cpo) {
    return (
      <ClientLayout>
        <Typography>No se encontró la orden solicitada.</Typography>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <Button onClick={() => navigate("/orders")} sx={{ mb: 2 }}>
        &larr; Volver a mis órdenes
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5">Orden {cpo.code}</Typography>
          {cpo.status && <Chip label={cpo.status.name} />}
        </Box>
        <Grid container spacing={2}>
          <Field label="Orden de trabajo" value={cpo.workOrder} />
          <Field
            label="Cantidad"
            value={cpo.totalQuantity ?? cpo.quantity}
          />
          <Field
            label="Cantidad enviada"
            value={cpo.totalShippedQuantity ?? cpo.shippedQuantity}
          />
          <Field label="Fecha de recepción" value={cpo.receptionDate} />
          <Field
            label="Fecha de entrega solicitada"
            value={cpo.requestedDeliveryDate}
          />
          <Field label="Last MAD" value={cpo.lastMad} />
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Progreso de la orden
        </Typography>
        {operationStatus ? (
          <OperationProcessStepper
            mode="view"
            steps={operationStatus.steps}
            activeStepId={operationStatus.currentStepId}
          />
        ) : (
          <Typography color="text.secondary">
            Esta orden aún no tiene un proceso de operación asignado.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Envíos
        </Typography>
        {shipments.length ? (
          shipments.map((shipment, index) => (
            <Box key={shipment.id}>
              {index > 0 && <Divider sx={{ my: 1 }} />}
              <Grid container spacing={2}>
                <Field label="No. de envío" value={shipment.shipmentNo} />
                <Field
                  label="Producto"
                  value={formatFinishedGood(shipment)}
                />
                <Field label="Cantidad" value={shipment.shippedQuantity} />
                <Field label="Fecha" value={shipment.shippedDate} />
              </Grid>
            </Box>
          ))
        ) : (
          <Typography color="text.secondary">Sin envíos registrados.</Typography>
        )}
      </Paper>
    </ClientLayout>
  );
}
